/**
 * utils/bordereauPdfRenderer.ts
 *
 * Shared bordereau PDF rendering helper consumed by both bordereauGenerator.ts
 * (standalone PDF export) and pdfDossierBuilder.ts (prepended bordereau page).
 *
 * Features:
 * - Dynamic cursor-based pagination: rows are measured before drawing so a new
 *   page is started whenever a row would overflow the bottom margin.
 * - Word-wrapped labels: every label drawText call includes maxWidth + lineHeight
 *   so long designations wrap instead of overflowing off the right edge.
 * - Supports both addPage (standalone) and insertPage (prepend into an existing
 *   PDFDocument) via the optional insertAtIndex parameter.
 *
 * Created Apr 2026: extracted from bordereauGenerator.ts and pdfDossierBuilder.ts
 * to eliminate duplication and centralise layout + overflow logic.
 * Updated Apr 2026: replaced approximate estimateLineCount with countWrappedLines
 * that replicates pdf-lib's exact greedy word-boundary wrapping algorithm so
 * pagination cursor arithmetic matches what drawText actually renders.
 * Updated Apr 2026: fixed lineWidth reset on word-wrap inside countWrappedLines
 * to use wordWidth (which includes the trailing space) instead of the bare word
 * width, matching pdf-lib's behaviour where the wrapped segment retains its
 * trailing-space width when starting the new line.
 */

import type { PDFDocument, PDFFont } from 'pdf-lib';
import { rgb } from 'pdf-lib';
import { sanitizeWinAnsi } from './pdfTextSanitizer';

const A4_W = 595.28;
const A4_H = 841.89;
const MARGIN = 70;
const PIECE_COL_X = MARGIN;
const LABEL_COL_X = MARGIN + 130;
const LABEL_MAX_WIDTH = A4_W - MARGIN - LABEL_COL_X; // 325.28 pt
const FONT_SIZE = 11;
const LINE_HEIGHT = FONT_SIZE * 1.3; // ~14.3 pt
const INTER_ROW_SPACE = 42 - LINE_HEIGHT; // ~27.7 pt – preserves original row gap for single-line rows
const TITLE_SIZE = 14;

export interface RenderBordereauPagesOptions {
  pdfDoc: PDFDocument;
  font: PDFFont;
  fontBold: PDFFont;
  pieces: Array<{ label: string }>;
  /** When provided, pages are inserted at this index (prepend mode). */
  insertAtIndex?: number;
}

/**
 * Counts the number of wrapped lines a label will produce using the same greedy
 * word-boundary algorithm that pdf-lib applies internally when drawText is called
 * with maxWidth. pdf-lib splits on spaces by default (wordBreaks: [' ']), then
 * greedily accumulates segments onto the current line until the next segment
 * would exceed maxWidth, at which point a new line begins. This mirrors that
 * behaviour exactly so pagination cursor arithmetic matches the rendered output.
 */
function countWrappedLines(label: string, font: PDFFont): number {
  if (!label) return 1;

  const segments = label.split(' ');
  let lines = 1;
  let lineWidth = 0;

  for (let i = 0; i < segments.length; i++) {
    const word = segments[i];
    const wordWidth = font.widthOfTextAtSize(
      i < segments.length - 1 ? word + ' ' : word,
      FONT_SIZE,
    );

    if (lineWidth + wordWidth > LABEL_MAX_WIDTH && lineWidth > 0) {
      lines++;
      lineWidth = wordWidth;
    } else {
      lineWidth += wordWidth;
    }
  }

  return lines;
}

function computeRowHeight(label: string, font: PDFFont): number {
  return countWrappedLines(label, font) * LINE_HEIGHT + INTER_ROW_SPACE;
}

/**
 * Renders the full bordereau into pdfDoc, creating as many pages as needed.
 * This function is synchronous – the caller must have already embedded the fonts.
 */
export function renderBordereauPages(options: RenderBordereauPagesOptions): void {
  const { pdfDoc, font, fontBold, pieces, insertAtIndex } = options;

  let insertedCount = 0;

  const createPage = () => {
    const p = insertAtIndex !== undefined
      ? pdfDoc.insertPage(insertAtIndex + insertedCount, [A4_W, A4_H])
      : pdfDoc.addPage([A4_W, A4_H]);
    insertedCount++;
    return p;
  };

  let currentPage = createPage();
  let cursorY = A4_H - MARGIN;

  const advancePage = () => {
    currentPage = createPage();
    cursorY = A4_H - MARGIN;
  };

  // ── Header (first page only) ──────────────────────────────────────────────
  const title = 'BORDEREAU DE PIECES COMMUNIQUEES';
  const titleWidth = fontBold.widthOfTextAtSize(title, TITLE_SIZE);
  currentPage.drawText(title, {
    x: (A4_W - titleWidth) / 2,
    y: cursorY - 20,
    size: TITLE_SIZE,
    font: fontBold,
    color: rgb(0.07, 0.07, 0.07),
  });
  cursorY -= 46;

  currentPage.drawLine({
    start: { x: MARGIN, y: cursorY },
    end: { x: A4_W - MARGIN, y: cursorY },
    thickness: 0.5,
    color: rgb(0.5, 0.5, 0.5),
  });
  cursorY -= 30;

  // ── Rows ─────────────────────────────────────────────────────────────────
  for (let i = 0; i < pieces.length; i++) {
    const pieceLabel = sanitizeWinAnsi(`Pi\u00e8ce ${i + 1}`);
    const designation = sanitizeWinAnsi(pieces[i].label);
    const rowH = computeRowHeight(designation, font);

    if (cursorY - rowH < MARGIN) {
      advancePage();
    }

    currentPage.drawText(pieceLabel, {
      x: PIECE_COL_X,
      y: cursorY,
      size: FONT_SIZE,
      font: fontBold,
      color: rgb(0.07, 0.07, 0.07),
    });

    currentPage.drawText(designation, {
      x: LABEL_COL_X,
      y: cursorY,
      size: FONT_SIZE,
      font,
      color: rgb(0.07, 0.07, 0.07),
      maxWidth: LABEL_MAX_WIDTH,
      lineHeight: LINE_HEIGHT,
    });

    cursorY -= rowH;
  }
}
